import { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setSelectedDataSource, updateDataSourceAlias } from '../store/slices/dataSourcesSlice';
import { fetchSchedulerTasks } from '../store/slices/schedulerSlice';
import { redirectToSalesforceAddDataSource, redirectToHubSpotAddDataSource } from '../api/auth';
import { api, AUTH_URL } from '../api/client';
import { repairAliasInViews } from '../api/analytics';
import { getDataSourceConfig } from './DataSourcesTab';

const ALIAS_REGEX = /^[a-zA-Z_][a-zA-Z0-9_]{0,19}$/;

/**
 * DataSourceSelector - Rail to select and view status of data sources
 *
 * Shows all available data sources for the current tenant with:
 * - Auth status (active, needs_reauth, expired)
 * - Sync status (synced objects count, last sync time)
 * - Provider type (Salesforce, HubSpot, etc.)
 * - Alias badge with inline editing
 */
export default function DataSourceSelector({ showAlways = false }) {
  const dispatch = useDispatch();
  const { items: dataSources, selectedDataSource } = useSelector((state) => state.dataSources);
  const { objects: syncedObjects } = useSelector((state) => state.analytics);
  const { tasks: schedulerTasks } = useSelector((state) => state.scheduler);
  const { activeTenant } = useSelector((state) => state.auth);
  const [showAddMenu, setShowAddMenu] = useState(false);

  // Fetch scheduler tasks once so last-run dates match the Pipelines page
  useEffect(() => {
    if (schedulerTasks.length === 0) {
      dispatch(fetchSchedulerTasks());
    }
  }, [dispatch, schedulerTasks.length]);

  // Alias editing state: { [data_source_id]: { editing, value, error, saving, savedAlias } }
  const [aliasState, setAliasState] = useState({});

  // Show when explicitly requested or when data sources exist
  if (!showAlways && dataSources.length === 0) {
    return null;
  }

  const handleSelect = (dataSource) => {
    dispatch(setSelectedDataSource(dataSource));
  };

  const handleConnectSalesforce = () => {
    redirectToSalesforceAddDataSource(activeTenant?.tenant_id);
  };

  const handleConnectHubSpot = () => {
    redirectToHubSpotAddDataSource(activeTenant?.tenant_id);
  };

  // Provider display name — sourced from the shared config in DataSourcesTab (plan §3.1).
  const getProviderDisplay = (sourceType) => getDataSourceConfig(sourceType).displayName;

  // Get auth status badge
  const getAuthBadge = (authStatus) => {
    if (authStatus === 'active') {
      return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">Connected</span>;
    }
    if (authStatus === 'needs_reauth') {
      return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">Needs Reauth</span>;
    }
    return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Not Connected</span>;
  };

  // Get sync status for a data source
  const getSyncStatus = (dataSource) => {
    // Count synced objects from sync cache
    const sourceObjects = (syncedObjects || []).filter(
      obj => obj.data_source_id === dataSource.data_source_id && obj.is_synced
    );

    // Use last pipeline run date (same source as Pipelines page) for consistency
    const schedulerTask = (schedulerTasks || []).find(
      t => t.data_source_id === dataSource.data_source_id
    );
    const lastRunAt = schedulerTask?.last_run?.started_at;

    if (sourceObjects.length === 0 && !lastRunAt) {
      return <span className="text-xs text-gray-500">Not synced</span>;
    }

    return (
      <span className="text-xs text-gray-600">
        {sourceObjects.length > 0
          ? `${sourceObjects.length} object${sourceObjects.length !== 1 ? 's' : ''} synced`
          : 'Not synced'}
        {lastRunAt && (
          <span className="text-gray-400 ml-1">
            ({formatRelativeTime(new Date(lastRunAt))})
          </span>
        )}
      </span>
    );
  };

  // Format relative time
  const formatRelativeTime = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get effective alias for a data source (edited value takes precedence over stored value)
  const getEffectiveAlias = (ds) => {
    const state = aliasState[ds.data_source_id];
    if (state && 'savedAlias' in state) return state.savedAlias;
    return ds.alias || null;
  };

  // Start editing alias for a data source
  const startEditAlias = (e, ds) => {
    e.stopPropagation(); // Don't trigger data source selection
    const currentAlias = getEffectiveAlias(ds);
    setAliasState(prev => ({
      ...prev,
      [ds.data_source_id]: {
        ...prev[ds.data_source_id],
        editing: true,
        value: currentAlias || '',
        error: null,
      },
    }));
  };

  // Cancel alias editing
  const cancelEditAlias = (e, dsId) => {
    e.stopPropagation();
    setAliasState(prev => ({
      ...prev,
      [dsId]: {
        ...prev[dsId],
        editing: false,
        error: null,
      },
    }));
  };

  // Save alias
  const saveAlias = async (e, ds) => {
    e.stopPropagation();
    const dsId = ds.data_source_id;
    const state = aliasState[dsId] || {};
    const newAlias = state.value?.trim() || null;

    // Validate
    if (newAlias && !ALIAS_REGEX.test(newAlias)) {
      setAliasState(prev => ({
        ...prev,
        [dsId]: {
          ...prev[dsId],
          error: 'Alias must start with a letter or underscore and contain only letters, digits, underscores (max 20 chars)',
        },
      }));
      return;
    }

    setAliasState(prev => ({
      ...prev,
      [dsId]: { ...prev[dsId], saving: true, error: null },
    }));

    try {
      const response = await api.put(`${AUTH_URL}/api/v1/tenant/data-sources/${encodeURIComponent(dsId)}/alias`, {
        alias: newAlias,
      });
      // Update Redux store so other components see the new alias immediately
      dispatch(updateDataSourceAlias({ dataSourceId: dsId, alias: newAlias }));

      // If alias changed, repair saved views that reference the old alias
      let viewsUpdated = 0;
      const oldAlias = response?.old_alias;
      if (oldAlias && oldAlias !== newAlias) {
        try {
          const repairResult = await repairAliasInViews(oldAlias, newAlias);
          viewsUpdated = repairResult?.views_updated ?? 0;
        } catch {
          // Repair failure is non-fatal — alias is already saved
        }
      }

      setAliasState(prev => ({
        ...prev,
        [dsId]: {
          ...prev[dsId],
          editing: false,
          saving: false,
          savedAlias: newAlias,
          error: null,
          viewsUpdated: viewsUpdated > 0 ? viewsUpdated : null,
        },
      }));
    } catch (err) {
      setAliasState(prev => ({
        ...prev,
        [dsId]: {
          ...prev[dsId],
          saving: false,
          error: err.message || 'Failed to save alias',
        },
      }));
    }
  };

  const handleAliasKeyDown = (e, ds) => {
    if (e.key === 'Enter') {
      saveAlias(e, ds);
    } else if (e.key === 'Escape') {
      cancelEditAlias(e, ds.data_source_id);
    }
  };

  // If no data sources yet, show placeholder
  if (dataSources.length === 0) {
    return (
      <div className="bg-blue-50 border-b border-blue-200 px-4 py-3">
        <div className="flex items-center gap-2 text-sm text-blue-800">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>No data sources connected. Connect Salesforce or HubSpot to get started.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border-b border-gray-200" data-testid="data-source-selector">
      <div className="px-4 py-3">
        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
          Data Sources
        </div>
        <div className="space-y-2" data-testid="data-sources-list">
          {dataSources.map((ds) => {
            const isSelected = selectedDataSource?.data_source_id === ds.data_source_id;
            const dsAliasState = aliasState[ds.data_source_id] || {};
            const effectiveAlias = getEffectiveAlias(ds);
            const isEditing = dsAliasState.editing;

            return (
              <button
                key={ds.data_source_id}
                data-testid={`data-source-item-${ds.data_source_id}`}
                onClick={() => handleSelect(ds)}
                className={`w-full text-left px-3 py-2 rounded-lg transition ${
                  isSelected
                    ? 'bg-primary-50 border-2 border-primary-500'
                    : 'bg-gray-50 border-2 border-transparent hover:border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium text-gray-900 text-sm truncate">
                        {ds.name}
                      </span>
                      {isSelected && (
                        <svg className="w-4 h-4 text-primary-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}

                      {/* Alias badge / inline edit */}
                      {isEditing ? (
                        <span
                          className="flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            autoFocus
                            type="text"
                            value={dsAliasState.value || ''}
                            onChange={(e) => setAliasState(prev => ({
                              ...prev,
                              [ds.data_source_id]: { ...prev[ds.data_source_id], value: e.target.value, error: null },
                            }))}
                            onKeyDown={(e) => handleAliasKeyDown(e, ds)}
                            onBlur={(e) => saveAlias(e, ds)}
                            placeholder="alias"
                            maxLength={20}
                            className="text-xs px-1.5 py-0.5 border border-gray-300 rounded font-mono w-24 focus:outline-none focus:border-primary-500"
                            disabled={dsAliasState.saving}
                          />
                          <button
                            onClick={(e) => saveAlias(e, ds)}
                            disabled={dsAliasState.saving}
                            className="text-xs text-green-600 hover:text-green-700 font-medium"
                            title="Save alias"
                          >
                            {dsAliasState.saving ? '...' : 'Save'}
                          </button>
                          <button
                            onClick={(e) => cancelEditAlias(e, ds.data_source_id)}
                            className="text-xs text-gray-400 hover:text-gray-600"
                            title="Cancel"
                          >
                            Cancel
                          </button>
                        </span>
                      ) : effectiveAlias ? (
                        <span
                          onClick={(e) => startEditAlias(e, ds)}
                          title="Click to edit alias"
                          className="px-1.5 py-0.5 text-xs font-mono bg-gray-200 text-gray-600 rounded cursor-pointer hover:bg-gray-300 transition"
                        >
                          {effectiveAlias}
                        </span>
                      ) : (
                        <button
                          onClick={(e) => startEditAlias(e, ds)}
                          className="text-xs text-gray-400 hover:text-gray-600 underline decoration-dotted"
                          title="Add alias for use in cross-source queries"
                        >
                          Add alias
                        </button>
                      )}
                    </div>

                    {/* Alias validation error */}
                    {dsAliasState.error && (
                      <div
                        className="text-xs text-red-600 mt-0.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {dsAliasState.error}
                      </div>
                    )}

                    {/* Views-updated feedback after alias rename */}
                    {dsAliasState.viewsUpdated != null && !isEditing && (
                      <div
                        className="text-xs text-green-600 mt-0.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {dsAliasState.viewsUpdated} saved view{dsAliasState.viewsUpdated !== 1 ? 's' : ''} updated to use new alias
                      </div>
                    )}

                    <div className="text-xs text-gray-500">
                      {getProviderDisplay(ds.source_type)}
                    </div>
                  </div>
                  {getAuthBadge(ds.auth_status)}
                </div>
                <div className="mt-1">
                  {getSyncStatus(ds)}
                </div>
              </button>
            );
          })}
        </div>

        {/* Add data source button */}
        <div className="relative mt-2">
          <button
            onClick={() => setShowAddMenu((v) => !v)}
            className="w-full flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-md transition"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add data source
          </button>
          {showAddMenu && (
            <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              <button
                onClick={() => { setShowAddMenu(false); handleConnectSalesforce(); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
              >
                <span className="w-5 h-5 bg-[#00A1E0] rounded flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.87 3.97c1.21-.55 2.56-.84 3.93-.84 2.24 0 4.31.78 5.92 2.09-.63.62-1.17 1.32-1.61 2.11-1.24-1.07-2.75-1.72-4.47-1.72-1.98 0-3.77.82-5.04 2.15-1.27-1.33-3.06-2.15-5.04-2.15-1.87 0-3.54.76-4.77 1.99-.15-.84-.22-1.67-.22-2.51 0-1.97.41-3.84 1.15-5.53C4.33 1.14 6.44.44 8.69.44c2.3 0 4.36.9 5.91 2.35.28-.09.55-.18.84-.25-.54-.28-1.11-.52-1.7-.71-.62.36-1.26.69-1.87 1.14zm8.04 10.05c0 3.82-3.1 6.92-6.92 6.92-3.82 0-6.92-3.1-6.92-6.92s3.1-6.92 6.92-6.92 6.92 3.1 6.92 6.92z" />
                  </svg>
                </span>
                Connect Salesforce
              </button>
              <button
                onClick={() => { setShowAddMenu(false); handleConnectHubSpot(); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg"
              >
                <span className="w-5 h-5 bg-[#FF7A59] rounded flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.978v-.072A2.198 2.198 0 0017.233.836h-.071a2.198 2.198 0 00-2.199 2.198v.071a2.199 2.199 0 001.267 1.979v2.846a4.242 4.242 0 00-2.344.774l-6.164-3.287a2.477 2.477 0 10-.913 1.062l6.164 3.287a4.315 4.315 0 00-.549 2.093 4.346 4.346 0 004.345 4.345 4.346 4.346 0 004.345-4.345A4.346 4.346 0 0018.164 7.93z"/>
                  </svg>
                </span>
                Connect HubSpot
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
